import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  AcademicCapIcon,
  BookOpenIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/solid';
import { authFetch, authService, apiUrl } from '../utils/api';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'لوحة التحكم', href: '/', icon: HomeIcon },
    { name: 'مستخدمي النظام', href: '/system-users', icon: UserGroupIcon },
    { name: 'الطلاب', href: '/students', icon: AcademicCapIcon },
    { name: 'الباقات', href: '/courses', icon: BookOpenIcon },
    { 
      name: 'حصص السنتر', 
      href: '/sessions', 
      icon: CalendarIcon
    },
    { 
      name: 'الكتب', 
      href: '/books', 
      icon: ({ className }: { className?: string }) => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      )
    },
    { 
      name: 'أكواد المحاضرات', 
      href: '/unit-codes', 
      icon: ({ className }: { className?: string }) => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      )
    },
    { 
      name: 'أكواد الباقات', 
      href: '/codes', 
      icon: ({ className }: { className?: string }) => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      )
    },
 
    { name: 'اتصل بنا', href: '/contact-us', icon: ChatBubbleLeftRightIcon },
  ];

  const handleLogout = async () => {
    try {
      console.log('Starting logout process...');
      // Clear auth data and redirect to login page
      await authService.logout();
      console.log('Logout completed, navigating to login...');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still logout on the client side even if there's an error
      await authService.logout();
      navigate('/login');
    }
  };

  return (
    <div className="w-64 h-full bg-gradient-to-b from-primary to-primary/90 shadow-lg" dir="rtl">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <h1 className="text-xl font-bold text-white">لوحة الإدارة</h1>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-md"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => onClose && onClose()}
                className={`${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                } group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200`}
              >
                {typeof item.icon === 'function' ? (
                  item.icon({ 
                    className: `${
                      isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                    } ml-3 flex-shrink-0 h-6 w-6 transition-colors duration-200`
                  })
                ) : (
                  <item.icon
                    className={`${
                      isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                    } ml-3 flex-shrink-0 h-6 w-6 transition-colors duration-200`}
                    aria-hidden="true"
                  />
                )}
                {item.name}
              </Link>
            );
          })}
        </nav>
        {/* Logout button */}
        <div className="px-2 py-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full text-white hover:bg-red-600 bg-red-500 group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon
              className="text-white ml-3 flex-shrink-0 h-6 w-6 transition-colors duration-200"
              aria-hidden="true"
            />
            تسجيل الخروج
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;